import React, { useEffect, useMemo, useRef, useState } from 'react'
import { convertAudioTime, getBlobDuration } from '../../utils/utils'
import useRecorder from 'shared/useRecorder'
import useMicLevels, { drawIdleWave, drawTakeWave } from 'shared/useMicLevels'
import MicBlockedNotice from 'shared/MicBlockedNotice'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { setAudioRecording } from '../../redux/actions/recorderAction'
import './recorder.css'

const MAX_RECORDING_MS = 5 * 60 * 1000
/** Learners leave the recorder running; after this much silence it stops by itself. */
const SILENCE_AUTO_STOP_MS = 20 * 1000

/**
 * Shared recorder (taster, teacher dashboard). Records the browser's real
 * format, draws a live waveform while recording, freezes it into the take as
 * the playback scrubber, stops itself after 20 s of silence or 5 minutes, and
 * shows a designed "microphone blocked" state instead of an alert.
 */
const Recorder: React.FC = (props: any) => {
  const { t } = useTranslation('writeSpeak')
  const { audioURL, isRecording, startRecording, stopRecording, mediaRecorder, error, clearError } = useRecorder()
  const [play, setPlay] = useState(false)
  const [record, setRecord] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [audio, setAudio] = useState('')
  const audioRef = useMemo(() => new Audio(audio), [audio])
  const maxRecordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const liveCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const takeCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const recordingStartedAtRef = useRef(0)
  const recordedSecondsRef = useRef(0)
  const [recordingLength, setRecordingLength] = useState<number>(0)

  const stopNow = () => {
    clearMaxRecordingTimeout()
    stopRecording(false)
  }

  const levelsRef = useMicLevels({
    stream: mediaRecorder?.stream,
    active: isRecording && !!mediaRecorder,
    canvasRef: liveCanvasRef,
    silenceMs: SILENCE_AUTO_STOP_MS,
    onSilence: stopNow,
  })

  const clearMaxRecordingTimeout = () => {
    if (maxRecordingTimeoutRef.current) {
      clearTimeout(maxRecordingTimeoutRef.current)
      maxRecordingTimeoutRef.current = null
    }
  }

  // Elapsed recording time, counted from the moment the microphone actually opened.
  useEffect(() => {
    if (!isRecording || !mediaRecorder) return
    recordingStartedAtRef.current = performance.now()
    setRecordingLength(0)
    const id = setInterval(() => {
      const secs = (performance.now() - recordingStartedAtRef.current) / 1000
      recordedSecondsRef.current = secs
      setRecordingLength(Math.floor(secs))
    }, 250)
    return () => clearInterval(id)
  }, [isRecording, mediaRecorder])

  // The picker failed to open the microphone: drop back to the resting layout.
  useEffect(() => {
    if (error) {
      clearMaxRecordingTimeout()
      setRecord(false)
    }
  }, [error])

  useEffect(() => {
    const onLoadedMetadata = async () => {
      if (audioRef.duration !== Infinity && !isNaN(audioRef.duration)) {
        setDuration(audioRef.duration)
      } else if (recordedSecondsRef.current) {
        // MediaRecorder WebM carries no duration header; the measured length is exact enough.
        setDuration(recordedSecondsRef.current)
      } else {
        try {
          setDuration(await getBlobDuration(audio))
        } catch (err) {
          setDuration(0)
        }
      }
    }
    const onEnded = () => {
      setCurrentTime(0)
      setPlay(false)
    }
    audioRef.addEventListener('loadedmetadata', onLoadedMetadata)
    audioRef.addEventListener('ended', onEnded)
    return () => {
      audioRef.pause()
      audioRef.removeEventListener('loadedmetadata', onLoadedMetadata)
      audioRef.removeEventListener('ended', onEnded)
    }
  }, [audioRef])

  // Smooth progress while playing (timeupdate only fires a few times a second).
  useEffect(() => {
    if (!play) return
    let raf = 0
    const tick = () => {
      setCurrentTime(audioRef.currentTime)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [play, audioRef])

  useEffect(() => {
    setAudio(audioURL.data)
    if (audioURL.data && recordedSecondsRef.current) setDuration(recordedSecondsRef.current)
    setAudioRecording(audioURL.file)
  }, [audioURL])

  const hasTake = Boolean(audio && audio !== '')
  const isLiveLayout = record && !hasTake

  // Draw the resting baseline or the frozen take whenever the layout or progress changes.
  useEffect(() => {
    if (isLiveLayout) return
    const progress = duration ? Math.min(1, currentTime / duration) : 0
    if (hasTake) drawTakeWave(takeCanvasRef.current, levelsRef.current, progress)
    else drawIdleWave(takeCanvasRef.current)
  }, [isLiveLayout, hasTake, currentTime, duration])

  useEffect(() => {
    const redraw = () => {
      if (isLiveLayout) return
      const progress = duration ? Math.min(1, currentTime / duration) : 0
      if (hasTake) drawTakeWave(takeCanvasRef.current, levelsRef.current, progress)
      else drawIdleWave(takeCanvasRef.current)
    }
    window.addEventListener('resize', redraw)
    return () => window.removeEventListener('resize', redraw)
  })

  const onRecording = async () => {
    if (hasTake) return

    if (!record) {
      clearMaxRecordingTimeout()
      clearError()
      startRecording()
      maxRecordingTimeoutRef.current = setTimeout(() => {
        stopRecording(false)
        maxRecordingTimeoutRef.current = null
      }, MAX_RECORDING_MS)
      setRecord(true)
    } else {
      clearMaxRecordingTimeout()
      stopRecording(false)
      setRecord(false)
    }
  }

  const onReset = async () => {
    clearMaxRecordingTimeout()
    stopRecording(true)
    setRecord(false)
    setPlay(false)
    setDuration(0)
    setCurrentTime(0)
    audioRef?.pause()
    setAudio('')
    levelsRef.current = []
  }

  useEffect(() => {
    if (!props?.currentAudioRecording.size || props?.currentAudioRecording.size === undefined) onReset()
  }, [props?.currentAudioRecording])

  useEffect(() => {
    return () => clearMaxRecordingTimeout()
  }, [])

  const onPlay = () => {
    if (!hasTake) return
    if (!play) {
      audioRef.play().catch(() => setPlay(false))
    } else {
      audioRef.pause()
    }
    setPlay(!play)
  }

  const seekTo = (clientX: number) => {
    const canvas = takeCanvasRef.current
    if (!canvas || !hasTake || !duration) return
    const rect = canvas.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    try {
      audioRef.currentTime = ratio * duration
    } catch (e) {}
    setCurrentTime(ratio * duration)
  }

  const onScrubKey = (e: React.KeyboardEvent) => {
    if (!hasTake || !duration) return
    const step = e.key === 'ArrowRight' ? 2 : e.key === 'ArrowLeft' ? -2 : 0
    if (!step) return
    e.preventDefault()
    const next = Math.min(duration, Math.max(0, currentTime + step))
    try {
      audioRef.currentTime = next
    } catch (err) {}
    setCurrentTime(next)
  }

  if (error) {
    return (
      <div className='audio-box recorder-v2'>
        <MicBlockedNotice error={error} onRetry={onRecording} />
      </div>
    )
  }

  return (
    <div className='audio-box recorder-v2'>
      <div className='section-audio recorder-row'>
        <div className='recorder-controls'>
          {/* Record, the hero control. Becomes the stop control while recording. */}
          <button
            type='button'
            className={`recorder-mic${isLiveLayout ? ' is-recording' : ''}${hasTake ? ' is-disabled' : ''}`}
            onClick={onRecording}
            disabled={hasTake}
            aria-label={isLiveLayout ? t('recorder.stop') : t('recorder.start')}
          >
            <i className={isLiveLayout ? 'fa fa-stop' : 'fa fa-microphone'}></i>
          </button>
          {!isLiveLayout && (
            <button
              type='button'
              className={`audio-play recorder-play${hasTake ? '' : ' is-disabled'}`}
              onClick={onPlay}
              disabled={!hasTake}
              aria-label={!play ? t('recorder.play') : t('recorder.pause')}
            >
              <i className={!play ? 'fa fa-play' : 'fa fa-pause'}></i>
            </button>
          )}
        </div>

        <div className='section-progress-bar recorder-track'>
          {isLiveLayout ? (
            <canvas ref={liveCanvasRef} className='recorder-waveform' aria-hidden='true' />
          ) : (
            <>
              <span>{convertAudioTime(currentTime)}</span>
              {/* The take's own waveform is the scrubber: it fills green as it plays. */}
              <canvas
                ref={takeCanvasRef}
                className={`recorder-waveform recorder-scrubber${hasTake ? ' is-seekable' : ''}`}
                role={hasTake ? 'slider' : undefined}
                tabIndex={hasTake ? 0 : -1}
                aria-label={hasTake ? t('recorder.position') : undefined}
                aria-hidden={hasTake ? undefined : true}
                aria-valuemin={hasTake ? 0 : undefined}
                aria-valuemax={hasTake ? Math.round(duration) : undefined}
                aria-valuenow={hasTake ? Math.round(currentTime) : undefined}
                onPointerDown={(e) => seekTo(e.clientX)}
                onKeyDown={onScrubKey}
              />
            </>
          )}
          <span>{isRecording ? convertAudioTime(recordingLength) : convertAudioTime(duration)}</span>
        </div>

        <button className='recorder-reset' onClick={() => setAudioRecording({})}>
          {t('recorder.reset')}
        </button>
      </div>
    </div>
  )
}

const mapStateToProps = (state: any) => ({
  currentAudioRecording: state.recorder.currentAudioRecording,
})

export default connect(mapStateToProps)(Recorder)
